<script setup lang="ts">
import { ref, type PropType, onMounted } from 'vue';
import InputText from 'primevue/inputtext';
import { DateTime } from 'luxon';
//@ts-expect-error types issue
import { VueSignaturePad } from 'vue-signature-pad';
import {
  type FormField,
  type Signatory,
  type SignatureData
} from '@/models/form';
import { useUserStore } from '@/stores/user';
import { ButtonType, CustomButton, SignatoryList } from '@/components';
import { formattedDate, DATE_FORMAT_3 } from '@/utils';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const userStore = useUserStore();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const emailInput = ref();
const localField = ref<FormField>(props.field);
const userSignature = ref();
const signaturePad = ref();
const showSignaturePad = ref(false);

onMounted(() => {
  if (localField.value.signatureValue) {
    const signatories = localField.value.signatureValue!.signatories;
    const index = signatories.findIndex(sign => sign.email === userStore.user?.email);
    if (index > 0) {
      userSignature.value = localField.value.signatureValue!.signatories[index];
    }
  }
});

const inviteSignatory = () => {
  if (localField.value.signatureValue) {
    const newSignature: Signatory = {
      signature: '',
      signatory: '',
      email: emailInput.value,
      dateTime: Math.round(DateTime.local().toSeconds())
    };
    localField.value.signatureValue.signatories.push(newSignature);
  } else {
    const signatories: Signatory[] = [];
    const newSignature: Signatory = {
      signature: '',
      signatory: '',
      email: emailInput.value,
      dateTime: Math.round(DateTime.local().toSeconds())
    };
    signatories.push(newSignature);
    const signatureData: SignatureData = {
      signatories
    };
    localField.value.signatureValue = signatureData;
  }
};

const onAddSignature = () => {
  if (signaturePad.value) {
    signaturePad.value.clearSignature();
  }
  showSignaturePad.value = !showSignaturePad.value;
};

const onUndoClick = () => {
  signaturePad.value.undoSignature();
};

const onClearClick = () => {
  signaturePad.value.clearSignature();
};

const onSaveClick = () => {
  const { data } = signaturePad.value.saveSignature();

  const signIndex = localField.value.signatureValue?.signatories.findIndex(sign => sign.email === userStore.user?.email);
  if (signIndex && signIndex > -1) {
    localField.value.signatureValue!.signatories[signIndex].signature = data;
    userSignature.value = localField.value.signatureValue?.signatories[signIndex];
  } else {
    //when the user is not requested for signature, we'll add signatory row for him
    if (!localField.value.signatureValue) {
      localField.value.signatureValue = {
        signatories: []
      };
    }

    const newSignature: Signatory = {
      signature: data,
      signatory: userStore.user?.name ?? '',
      email: userStore.user?.email ?? '',
      dateTime: Math.round(DateTime.local().toSeconds()),
      emailSent: true
    };

    localField.value.signatureValue.signatories.push(newSignature);
    userSignature.value = newSignature;
  }
  onAddSignature();
};

const onRevoke = () => {
  userSignature.value = undefined;
};
</script>

<template>
  <div class="item-root">
    <CustomButton :text="$t('enter_sign')"
      class="signature-button"
      v-if="!userSignature && !showSignaturePad && !previewMode"
      @onClick="onAddSignature"
      :icon="{ style: 'far', icon: 'signature' }"
      :type="ButtonType.OUTLINED"/>
    <div class="signature" v-if="userSignature">
      <img :src="
        userSignature.signature.includes('data:image/png;base64') ?
          userSignature.signature :
          `data:image/png;base64,${userSignature.signature}`"
        alt="Captured signature"
        class="signature-image"/>
      <div>{{ userSignature.signatory }}</div>
      <div>
        {{
          formattedDate(
            DateTime.fromSeconds(userSignature.dateTime).toJSDate(),
            DATE_FORMAT_3
          )
        }}
      </div>
    </div>
    <div class="signature-pad" v-if="showSignaturePad">
      <CustomButton :text="$t('undon')"
        class="undo-button"
        :icon="{ style: 'far', icon: 'arrow-rotate-left' }"
        @onClick="onUndoClick"
        :type="ButtonType.OUTLINED"/>
      <VueSignaturePad ref="signaturePad" height="300px"/>
      <div class="signature-buttons">
        <CustomButton :text="$t('cancel')"
          class="button"
          @onClick="onAddSignature"
          :type="ButtonType.OUTLINED"/>
        <CustomButton :text="$t('clear')"
          class="button"
          @onClick="onClearClick"
          :margin-left="16"
          :type="ButtonType.OUTLINED"/>
        <CustomButton :text="$t('save')"
          class="save-button"
          :margin-left="16"
          @onClick="onSaveClick"
          :type="ButtonType.POSITIVE"/>
      </div>
    </div>
    <div class="invite" v-if="!previewMode">
      <div class="signature-divider">
        <div class="left-line" />
        <div class="or-label">{{ $t('or') }}</div>
        <div class="right-line" />
      </div>
      <div class="invite-signatory">
        <InputText id="input"
          :placeholder="$t('enter_email')"
          v-model="emailInput"
          class="email-input"
          type="text"/>
        <CustomButton :text="$t('invite_signatory')"
          class="invite-button"
          :margin-left="16"
          @onClick="inviteSignatory"
          :type="ButtonType.OUTLINED"/>
      </div>
    </div>
    <SignatoryList :field="localField" @onRevoke="onRevoke" :previewMode="previewMode"/>
  </div>
</template>

<style scoped lang="scss">
.signature-pad {
  border: 1px solid rgba(0, 0, 0, 0.25);
  max-width: 500px;
}
.undo-button {
  margin: 16px;
}
.signature-buttons {
  display: flex;
  margin: 16px;
}
.invite {
  max-width: 400px;
  padding: 20px 0px;
}
.signature-image {
  border: 1px solid rgba(0, 0, 0, 0.25);
  max-width: 220px;
}
.signature-divider {
  display: flex;
  align-items: center;
  padding: 20px 0px;

  .left-line,
  .right-line {
    width: 100%;
    height: 1px;
    background-color: #b8c2cc;
  }

  .or-label {
    font-size: 14px;
    color: #b8c2cc;
    padding: 0px 20px;
  }
}
.invite-signatory {
  display: flex;

  .email-input {
    flex: 1;
    font-size: 14px;
  }
}
</style>
